import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { ChangesPanel } from '@client/components/compliance-audit-templates/changes/ChangesPanel';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

export const Route = createFileRoute(
    '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/changes',
)({
    staticData: {
        ui: { useParentTitle: false },
    },
    // `base` = an explicit earlier PUBLISHED version to compare against. It
    // lives on this route — not on the details route, and not retained across
    // tabs — so switching target (VersionSelect navigates with `search:
    // { version }` alone) or tab drops it, and a new target always starts from
    // its predecessor.
    validateSearch: zodValidator(z.object({ base: z.number().int().positive().optional() })),
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ChangesTab />
        </PageController>
    );
}

/**
 * CUR-789 — what changed between template versions. The `version` search param
 * follows the details page's snapshot semantics: viewing published vN compares
 * it against its predecessor; viewing the working draft (no param) compares it
 * against the latest published version. `base` picks an explicit earlier
 * published version instead; a hand-edited invalid one surfaces the server's
 * error rather than silently falling back.
 */
function ChangesTab() {
    const { complianceAuditTemplateId } = Route.useParams();
    const { version, base } = Route.useSearch();

    const compareQuery = trpc.admin.complianceAuditTemplate.compareComplianceAuditTemplateVersions.useQuery({
        complianceAuditTemplateId,
        version,
        baseVersion: base,
    });
    // The details route already holds this query for the page — served from
    // cache; it only feeds the "Current" tag on the base picker.
    const templateQuery = trpc.admin.complianceAuditTemplate.getComplianceAuditTemplateVersion.useQuery({
        complianceAuditTemplateId,
        version,
    });

    if (compareQuery.isPending) {
        return <Loading />;
    }

    if (compareQuery.error) {
        return (
            <InlineQueryError
                error={compareQuery.error}
                onRetry={() => void compareQuery.refetch()}
                title="Couldn't load the version changes"
            />
        );
    }

    return (
        <ChangesPanel
            key={`${compareQuery.data.target.version}-${compareQuery.data.base?.version ?? 'first'}`}
            result={compareQuery.data}
            complianceAuditTemplateId={complianceAuditTemplateId}
            latestPublishedVersion={templateQuery.data?.latestPublishedVersion ?? null}
        />
    );
}
